import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Calendar, Clock, ChevronRight,
  Loader2, CalendarX, CheckCircle, XCircle, AlertCircle
} from 'lucide-react';
import { format, isPast, isToday } from 'date-fns';
import { useAuth } from '../context/AuthContext';
import { getCustomerBookings } from '../services/firestore';
import { Booking } from '../types';

const MyBookings: React.FC = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'upcoming' | 'past'>('upcoming');

  const fetchBookings = useCallback(async () => {
    if (!user) return;
    
    setLoading(true);
    try {
      const data = await getCustomerBookings(user.uid);
      setBookings(data);
    } catch (error) {
      console.error('Error fetching bookings:', error);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/auth', { state: { returnTo: '/bookings' } });
      return;
    }

    if (user) {
      fetchBookings();
    }
  }, [user, authLoading, navigate, fetchBookings]);

  const getStatusIcon = (status: Booking['status']) => {
    switch (status) {
      case 'confirmed':
        return <CheckCircle size={16} className="text-green-500" />;
      case 'pending':
        return <AlertCircle size={16} className="text-amber-500" />;
      case 'cancelled':
        return <XCircle size={16} className="text-red-500" />;
      case 'completed':
        return <CheckCircle size={16} className="text-gray-500" />;
      default:
        return null;
    }
  };

  const getStatusLabel = (status: Booking['status']) => {
    return status.charAt(0).toUpperCase() + status.slice(1);
  };

  const upcomingBookings = bookings.filter(b => {
    const bookingDate = new Date(b.date);
    return !isPast(bookingDate) || isToday(bookingDate);
  });

  const pastBookings = bookings.filter(b => {
    const bookingDate = new Date(b.date);
    return isPast(bookingDate) && !isToday(bookingDate);
  });

  const displayBookings = activeTab === 'upcoming' ? upcomingBookings : pastBookings;

  if (authLoading) {
    return (
      <div className="min-h-screen bg-prim-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-primary animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-prim-background">
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl font-display font-bold text-prim-text mb-2">
            My Bookings
          </h1>
          <p className="text-gray-500">
            View and manage your appointments
          </p>
        </div>

        {/* Tabs */}
        <div className="flex space-x-2 mb-6">
          <button
            onClick={() => setActiveTab('upcoming')}
            className={`px-5 py-2.5 rounded-xl font-medium transition-all ${
              activeTab === 'upcoming'
                ? 'bg-primary text-white'
                : 'bg-white text-gray-700 border border-gray-200 hover:border-gray-300'
            }`}
          >
            Upcoming ({upcomingBookings.length})
          </button>
          <button
            onClick={() => setActiveTab('past')}
            className={`px-5 py-2.5 rounded-xl font-medium transition-all ${
              activeTab === 'past'
                ? 'bg-primary text-white'
                : 'bg-white text-gray-700 border border-gray-200 hover:border-gray-300'
            }`}
          >
            Past ({pastBookings.length})
          </button>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="flex flex-col items-center justify-center py-20">
            <Loader2 className="w-8 h-8 text-primary animate-spin mb-4" />
            <p className="text-gray-500">Loading your bookings...</p>
          </div>
        )}

        {/* Empty State */}
        {!loading && displayBookings.length === 0 && (
          <div className="bg-white rounded-2xl border border-gray-100 p-12 text-center">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gray-100 flex items-center justify-center">
              <CalendarX size={32} className="text-gray-400" />
            </div>
            <h3 className="text-lg font-semibold text-prim-text mb-2">
              No {activeTab} bookings
            </h3>
            <p className="text-gray-500 mb-6">
              {activeTab === 'upcoming' 
                ? "You don't have any upcoming appointments."
                : "You don't have any past appointments."
              }
            </p>
            {activeTab === 'upcoming' && (
              <button
                onClick={() => navigate('/explore')}
                className="px-6 py-3 bg-primary text-white rounded-xl font-medium hover:bg-primary-dark transition-colors"
              >
                Book an Appointment
              </button>
            )}
          </div>
        )}

        {/* Bookings List */}
        {!loading && displayBookings.length > 0 && (
          <div className="space-y-4">
            {displayBookings.map((booking, index) => {
              const bookingDate = new Date(booking.date);
              const isUpcoming = !isPast(bookingDate) || isToday(bookingDate);

              return (
                <motion.div
                  key={booking.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className="bg-white rounded-2xl border border-gray-100 overflow-hidden hover:shadow-md transition-shadow"
                >
                  <div className="p-4 sm:p-6">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                      {/* Booking Info */}
                      <div className="flex-1">
                        {/* Status Badge */}
                        <div className="flex items-center space-x-2 mb-3">
                          {getStatusIcon(booking.status)}
                          <span className={`text-sm font-medium ${
                            booking.status === 'confirmed' ? 'text-green-600' :
                            booking.status === 'pending' ? 'text-amber-600' :
                            booking.status === 'cancelled' ? 'text-red-600' :
                            'text-gray-600'
                          }`}>
                            {getStatusLabel(booking.status)}
                          </span>
                          {isToday(bookingDate) && isUpcoming && (
                            <span className="px-2 py-0.5 bg-primary-light text-primary text-xs font-medium rounded-full">
                              Today
                            </span>
                          )}
                        </div>

                        {/* Date & Time */}
                        <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600 mb-3">
                          <div className="flex items-center">
                            <Calendar size={16} className="mr-2 text-primary" />
                            {format(bookingDate, 'EEE, MMM d, yyyy')}
                          </div>
                          <div className="flex items-center">
                            <Clock size={16} className="mr-2 text-primary" />
                            {booking.time}
                          </div>
                        </div>

                        {/* Services */}
                        <div className="text-sm text-gray-500">
                          {booking.services.map(s => s.service.name).join(', ')}
                        </div>
                      </div>

                      {/* Price & Action */}
                      <div className="flex items-center justify-between sm:flex-col sm:items-end gap-2">
                        <span className="text-xl font-bold text-prim-text">
                          £{booking.totalPrice.toFixed(2)}
                        </span>
                        
                        {isUpcoming && booking.status !== 'cancelled' && (
                          <button className="text-primary text-sm font-medium hover:underline flex items-center">
                            View Details
                            <ChevronRight size={16} className="ml-1" />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Action Bar for Upcoming */}
                  {isUpcoming && booking.status === 'confirmed' && (
                    <div className="px-4 sm:px-6 py-3 bg-gray-50 border-t border-gray-100 flex justify-end space-x-3">
                      <button className="px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-800 transition-colors">
                        Reschedule
                      </button>
                      <button className="px-4 py-2 text-sm font-medium text-red-600 hover:text-red-700 transition-colors">
                        Cancel
                      </button>
                    </div>
                  )}
                </motion.div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default MyBookings;

